import { ref, watch, computed } from '@vue/composition-api'
import store from '@/store'
import useNotifications from "@/composables/useNotifications";
import i18n from '@/libs/i18n';
import useCommon from '@/views/organization/useCommon'
import { useLambda } from '@/views/habit/awsLambda'
import { oid, date } from '@/helpers/mongoHelpers'

export default function useLocationList() {
  const { showSuccessMessage, showErrorMessage } = useNotifications()
  const { handleError } = useCommon()
  const { updateItem, getItemsAggregate } = useLambda()
  const userData = store.state?.userStore?.userData
  const clientId = userData.role !== "admin" ? userData.client.$oid : null
  const locations = ref([])
  const zoneAgencyFilter = JSON.parse(localStorage.getItem("clientData") || '{}').zone_agency_filter
  const commitmentFunctionality = JSON.parse(localStorage.getItem("clientData") || '{}').commitment_functionality
  const collection = 'location'

  // Table Headers
  const tableColumns = computed(() => {
    const headers = [
      { key: 'location', label: i18n.t('message.tableHeader.location'), sortable: true },
      { key: 'physical_location', label: i18n.t('message.tableHeader.physical_location'), sortable: true },
      { key: 'area', label: i18n.t('message.tableHeader.area'), sortable: true },
      { key: 'Actions', label: i18n.t('message.tableHeader.actions') },
    ]
    if (zoneAgencyFilter) {
      headers.splice(1, 0, { key: 'zone', label: i18n.t('message.tableHeader.zone'), sortable: true })
      if (!commitmentFunctionality) {
        // Insert in the position after 'zone'
        headers.splice(2, 0, { key: 'agency', label: i18n.t('message.tableHeader.agency'), sortable: true })
      }
    }
    return headers
  })

  const totalLocations = ref(0)
  const perPage = ref(10)
  const currentPage = ref(1)
  const perPageOptions = [5, 10, 15, 20, 25, 30, 40, 50, 100]
  const searchQuery = ref('')
  const defaultSortBy = 'location'
  const sortBy = ref(defaultSortBy)
  const isSortDirDesc = ref(false)
  const isLoading = ref(true)

  const fetchLocations = async (searchValue = '') => {
    isLoading.value = true

    try {
      const query = {
        client_id: oid(clientId),
        deleted: { $ne: true },
      }

      const pipeline = [
        { $match: query },
        {
          $lookup: {
            from: 'area',
            localField: 'area',
            foreignField: '_id',
            as: 'areaData'
          }
        },
        {
          $addFields: {
            area: { $arrayElemAt: ['$areaData.name', 0] }
          }
        },
        {
          $lookup: {
            from: 'physical_location',
            localField: 'physical_location',
            foreignField: '_id',
            as: 'physicalLocationData'
          }
        },
        {
          $addFields: {
            physical_location: { $arrayElemAt: ['$physicalLocationData.name', 0] }
          }
        },
        {
          $project: {
            areaData: 0,
            physicalLocationData: 0
          }
        }
      ]

      const items = await getItemsAggregate({ collection, pipeline })
      if (!items) return
  
      // Search
      if (searchValue && searchValue !== searchQuery.value) return

      locations.value  = searchQuery.value ? items.filter(e => e.location?.toLowerCase().includes(searchQuery.value.toLowerCase())) : items
      totalLocations.value = locations.value.length
    
    } catch (error) {
      console.log(error)
      handleError({ error, defaultMessage: i18n.t('message.location_list_fetch_error') })
    } finally {
      isLoading.value = false
    }
  }

  const searchLocation = searchValue => {
    isLoading.value = true
    fetchLocations(searchValue)
  }

  const deleteLocation = async (id) => {
    try {
      const query = { _id: oid(id) }
      const action = { $set: { deleted: true } }

      await updateItem({ collection, query, action })

      fetchLocations()
      showSuccessMessage(i18n.t('message.location_remove'))
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.something_went_wrong'))
    }
  }

  const showingMessage = computed(() => {
    const from = ((currentPage.value * perPage.value) - perPage.value) + (locations.value.length ? 1 : 0)
    const to = locations.value.length + ((currentPage.value * perPage.value) - perPage.value)

    return i18n.tc('message.paginationText', 0, { from: from, to: to, total: totalLocations.value })
  })

  watch([currentPage, perPage], () => {
    fetchLocations()
  })

  watch([searchQuery, sortBy, isSortDirDesc], () => {
    currentPage.value = 1
    fetchLocations()
  })

  return {
    fetchLocations,
    tableColumns,
    perPage,
    currentPage,
    perPageOptions,
    totalLocations,
    showingMessage,
    searchQuery,
    sortBy,
    isSortDirDesc,
    locations,
    deleteLocation,
    isLoading,
    searchLocation
  }
}